import { Directive, EventEmitter, Injector, OnDestroy, OnInit, Output } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { FilterManagerService } from './filter-manager.service';
import { IFilter } from './filter.interfaces';

@Directive()
export abstract class BaseFilterComponent<T = any> implements OnInit, OnDestroy, IFilter<T> {
  @Output() changeFilters = new EventEmitter<any>();

  isCollapsed = true;
  protected ngUnsubscribe = new Subject<void>();
  protected appliedFilters: any = {};
  protected router: Router;
  protected activatedRoute: ActivatedRoute;
  protected filterManager: FilterManagerService;
  private isUpdatingParams = false;

  abstract readonly filterId: string;

  constructor(protected injector: Injector) {
    this.router = injector.get(Router);
    this.activatedRoute = injector.get(ActivatedRoute);
    this.filterManager = injector.get(FilterManagerService);
  }

  ngOnInit(): void {
    this.filterManager.registerFilter(this);

    this.activatedRoute.queryParams
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((params) => {
        if (this.isUpdatingParams) {
          return;
        }
        const paramValue = params[this.getQueryParamKey()];
        this.clearSelection();
        if (paramValue) {
          this.parseQueryParam(paramValue);
          this.applyFilterSilently();
        }
      });
  }

  ngOnDestroy(): void {
    this.filterManager.unregisterFilter(this.filterId);
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  // --- Abstract methods subclasses must implement ---

  abstract getQueryParamKey(): string;

  abstract parseQueryParam(rawValue: string): void;

  abstract buildFilterValue(): any[];

  abstract clearSelection(): void;

  // --- Core filter lifecycle ---

  onFilterChange(): void {
    const filterValue = this.buildFilterValue();
    this.appliedFilters[this.getQueryParamKey()] = filterValue;
    this.updateQueryParams();
    this.emitFilters();
    this.filterManager.notifyFilterChange(this.filterId, filterValue);
  }

  resetFilters(): void {
    this.clearSelection();
    this.appliedFilters = {};
    this.emitFilters();
  }

  // --- IFilter interface ---

  getValue(): T | null {
    const val = this.appliedFilters[this.getQueryParamKey()];
    return (val && val.length > 0) ? val : null;
  }

  setValue(value: T | null): void {
    if (value) {
      this.appliedFilters[this.getQueryParamKey()] = value;
    } else {
      this.clearSelection();
      this.appliedFilters = {};
    }
  }

  reset(): void {
    this.resetFilters();
  }

  hasValue(): boolean {
    const val = this.appliedFilters[this.getQueryParamKey()];
    return val && val.length > 0;
  }

  getDisplayValue(): string {
    const val = this.appliedFilters[this.getQueryParamKey()];
    return val ? val.join(', ') : '';
  }

  getFilterLabel(): string {
    return this.filterId;
  }

  // --- Protected helpers ---

  protected updateQueryParams(): void {
    const key = this.getQueryParamKey();
    const value = this.appliedFilters[key];
    this.isUpdatingParams = true;

    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: { [key]: value?.length ? value.join(',') : null },
      queryParamsHandling: 'merge',
      // This is needed to prevent displaying the loading overlay
      // when the user selects a filter
      state: {
        skipCompanyInfoReload: true,
      }
    }).then(() => {
      this.isUpdatingParams = false;
    });
  }

  protected emitFilters(): void {
    this.changeFilters.emit(this.appliedFilters);
  }

  private applyFilterSilently(): void {
    const filterValue = this.buildFilterValue();
    this.appliedFilters[this.getQueryParamKey()] = filterValue;
    this.emitFilters();
    this.filterManager.notifyFilterChange(this.filterId, filterValue);
  }
}
